import { AQLogger } from "aq-logger";
import { PitangoMarketCommon } from "pitango-market-common";
import {
    AuthApi,
    type UsersPayloadIUpdateOrderChangePolicyRequest,
    type UsersResponseAuthResponse,
    type UsersResponseLoginResponse,
    type UsersResponseRegisterResponse,
    type UsersResponseResendConfirmationResponse
} from "../../../generated/api-client";
import { generatedConfiguration, ResponseError } from "@/services/api/generated-client";
import type { LocaleCode } from "@/i18n/locales";
import { GENERAL_USER_ERROR_MESSAGE } from "@/services/api/service-errors";
import { hashPassword, normalizeEmail } from "./password";

import DTO = PitangoMarketCommon.DTO;

const logger = new AQLogger("AuthApi");

const client = new AuthApi(generatedConfiguration);

export interface SignInParams {
    email: string;
    password: string;
}

export interface RegisterParams {
    email: string;
    password: string;
    displayName: string;
    languageCode: LocaleCode;
    mobilePhone?: string;
}

/**
 * Session/account boundary for the storefront. Mirrors the request contract
 * `client/src/services/users/users.service.tsx` already uses against the
 * same API, so the server side needs no changes to support the storefront.
 */
export const authApi = {
    /** Resolves the current session's user, or `null` if signed out. */
    async authenticate(): Promise<DTO.UserView | null> {
        try {
            logger.debug("Authenticating user...");
            const response: UsersResponseAuthResponse = await client.authenticateUser();
            if (response.reject) return null;
            logger.success("User authenticated.");
            return response.data?.user ?? null;
        } catch (error) {
            if (error instanceof ResponseError && (error.response.status === 401 || error.response.status === 403)) return null;
            throw logger.exception("Failed to authenticate user.", error);
        }
    },

    async signIn(params: SignInParams): Promise<UsersResponseLoginResponse> {
        try {
            logger.debug("Signing in user...");
            const email = normalizeEmail(params.email);
            const password = await hashPassword(params.password, email);
            const response = await client.loginUser({ usersPayloadILoginRequest: { email, password } });
            logger.success("User signed in.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to sign in.", error);
        }
    },

    async signOut(): Promise<void> {
        try {
            logger.debug("Signing out user...");
            const response = await client.logoutUser();
            logger.success("User signed out.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to sign out.", error);
        }
    },

    async register(params: RegisterParams): Promise<UsersResponseRegisterResponse> {
        try {
            logger.debug("Registering user...");
            const email = normalizeEmail(params.email);
            const password = await hashPassword(params.password, email);
            const response = await client.usersRegister({
                usersPayloadIRegisterRequest: {
                    email,
                    password,
                    displayName: params.displayName,
                    languageCode: params.languageCode,
                    mobilePhone: params.mobilePhone
                }
            });
            logger.success("User registered.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to register user.", error);
        }
    },

    async resendConfirmation(email: string, languageCode: LocaleCode): Promise<UsersResponseResendConfirmationResponse> {
        try {
            logger.debug("Resending confirmation email...");
            const response = await client.resendConfirmation({
                usersPayloadIResendConfirmationRequest: {
                    email: normalizeEmail(email),
                    languageCode
                }
            });
            logger.success("Confirmation email resent.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to resend confirmation email.", error);
        }
    },

    async forgotPassword(email: string, languageCode: LocaleCode) {
        try {
            logger.debug("Requesting password reset...");
            const response = await client.forgotPassword({ usersPayloadIForgotPasswordRequest: { email: normalizeEmail(email), languageCode } });
            logger.success("Password reset requested.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to request password reset.", error);
        }
    },

    async resetPassword(token: string, email: string, password: string) {
        try {
            logger.debug("Resetting password...");
            const normalizedEmail = normalizeEmail(email);
            const response = await client.usersResetPassword({ usersPayloadIResetPasswordRequest: {
                token, email: normalizedEmail, password: await hashPassword(password, normalizedEmail)
            } });
            logger.success("Password reset successfully.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to reset password.", error);
        }
    },

    async confirmEmail(token: string) {
        try {
            logger.debug("Confirming email...");
            const response = await client.usersConfirmEmail({ token });
            logger.success("Email confirmed.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to confirm email.", error);
        }
    },

    async userSetup(token: string, password: string) {
        try {
            logger.debug("Completing user setup...");
            const response = await client.usersUserSetup({
                usersPayloadIUserSetupRequest: {
                    token, password
                }
            });
            logger.success("User setup completed.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to complete user setup.", error);
        }
    },

    async updatePreferences(preferredLanguageCode: LocaleCode) {
        try {
            logger.debug("Updating preferences...");
            const response = await client.usersUpdatePreferences({ usersPayloadIUpdatePreferencesRequest: { preferredLanguageCode } });
            logger.success("Preferences updated.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to update preferences.", error);
        }
    },

    async getOrderChangePolicy() {
        try {
            logger.debug("Fetching order change policy...");
            const response = await client.usersGetOrderChangePolicy();
            logger.success("Order change policy fetched.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to fetch order change policy.", error);
        }
    },

    async updateOrderChangePolicy(policy: UsersPayloadIUpdateOrderChangePolicyRequest) {
        try {
            logger.debug("Updating order change policy...");
            const response = await client.usersUpdateOrderChangePolicy({ usersPayloadIUpdateOrderChangePolicyRequest: policy });
            logger.success("Order change policy updated.");
            return response;
        } catch (error) {
            throw logger.exception("Failed to update order change policy.", error);
        }
    }
};
